'use client';

import Script from 'next/script';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, Suspense } from 'react';

/**
 * Google Analytics 4 (GA4) Component - Page Tracking
 *
 * Tracks page views on route changes
 * Must be wrapped in Suspense due to useSearchParams
 */
function GoogleAnalyticsPageTracking() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID) {
      return;
    }

    // Track page views on route changes
    const url = pathname + searchParams.toString();

    if (window.gtag) {
      window.gtag('config', process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID, {
        page_path: url,
      });
    }
  }, [pathname, searchParams]);

  return null;
}

/**
 * Google Analytics 4 (GA4) Component
 *
 * Tracks page views, events, and user interactions
 * Integrates with Google AdSense for combined reporting
 */
export function GoogleAnalytics() {
  // Don't render if GA ID is not configured
  if (!process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID) {
    return null;
  }

  return (
    <>
      {/* Global Site Tag (gtag.js) - Google Analytics */}
      <Script
        strategy="afterInteractive"
        src={`https://www.googletagmanager.com/gtag/js?id=${process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID}`}
      />
      <Script
        id="google-analytics"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());

            gtag('config', '${process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID}', {
              page_path: window.location.pathname,
              send_page_view: true
            });

            ${process.env.NEXT_PUBLIC_GOOGLE_ADSENSE_ID ? `
            // Link AdSense with Analytics for combined reporting
            gtag('config', '${process.env.NEXT_PUBLIC_GOOGLE_ADSENSE_ID}');
            ` : ''}
          `,
        }}
      />

      {/* Page view tracking wrapped in Suspense */}
      <Suspense fallback={null}>
        <GoogleAnalyticsPageTracking />
      </Suspense>
    </>
  );
}

/**
 * Custom event tracking helper
 * Use this to track custom events throughout your application
 *
 * @example
 * trackEvent('add_to_watchlist', { media_type: 'movie', media_id: 123 });
 */
export const trackEvent = (
  eventName: string,
  eventParams?: Record<string, any>
) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
};

/**
 * Track page view manually
 * Useful for client-side navigation
 *
 * @example
 * trackPageView('/movie/123');
 */
export const trackPageView = (url: string) => {
  if (typeof window !== 'undefined' && window.gtag && process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID) {
    window.gtag('config', process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID, {
      page_path: url,
    });
  }
};

/**
 * Track user properties
 *
 * @example
 * trackUserProperty({ user_type: 'premium' });
 */
export const trackUserProperty = (properties: Record<string, any>) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('set', 'user_properties', properties);
  }
};

// Extend window type for TypeScript
declare global {
  interface Window {
    gtag: (
      command: string,
      targetId: string,
      config?: Record<string, any>
    ) => void;
    dataLayer: any[];
  }
}
